import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { DocumentNode, useQuery } from "@apollo/client";
import { BusinessBasic, BusinessData } from "../../types";
import { addBusinesses, setSelectedBusiness } from "../actions/businessActions";
import {
  BusinessQueryNames,
  GET_BUSINESSES,
  GET_MY_BUSINESSES,
} from "../../graphql/queries";
import { useAuthStatus } from "../../util/useAuthStatus";
import { UserContext } from "../../components/auth/auth-context";

const queryDocuments: { [key in BusinessQueryNames]: DocumentNode } = {
  [BusinessQueryNames.GET_MY_BUSINESSES]: GET_MY_BUSINESSES,
  [BusinessQueryNames.GET_BUSINESSES]: GET_BUSINESSES,
};

export const normalizeBusinessData = (input: any | any[]): BusinessData[] => {
  const dataList = Array.isArray(input) ? input : [input];

  return dataList.map((business) => {
    const {
      __typename,
      id,
      businessCategoryId,
      businessSubcategoryId,
      name,
      description,
      currency,
      status,
      street,
      cityId,
      countryId,
      stateId,
      zipcode,
      cityName,
      stateName,
      stateCode,
      address,
      lat,
      lng,
      hasLocation,
      phoneNumbers,
      faxNumber,
      emails,
      url,
      links,
      images,
      staffs,
      stage,
      createdBy,
      exemptedPlanId,
      exemptedPlanExpiresAt,
      roles,
      plans,
      displayOnBookingPage,
      businessAddress,
      workingHourGroups,
      businessBookingTypes,
      claimStatus,
      careMeta,
      careSettings,
      residents,
      ...rest
    } = business;

    const businessBasic: BusinessBasic = {
      id: Number(id),
      businessCategoryId: Number(businessCategoryId),
      businessSubcategoryId:
        businessSubcategoryId === null || businessSubcategoryId === undefined
          ? null
          : Number(businessSubcategoryId),
      name,
      description,
      currency,
      status,
      street,
      cityId: Number(cityId),
      countryId: Number(countryId),
      stateId: Number(stateId),
      zipcode,
      cityName,
      stateName,
      stateCode,
      address,
      lat,
      lng,
      hasLocation,
      stage,
      createdBy: Number(createdBy),
      exemptedPlanId:
        exemptedPlanId === null || exemptedPlanId === undefined
          ? null
          : Number(exemptedPlanId),
      exemptedPlanExpiresAt: exemptedPlanExpiresAt || null,
      phoneNumbers:
        phoneNumbers?.map((p: any) => ({
          countryCode: p.countryCode,
          phoneNumber: p.phoneNumber,
        })) ?? [],
      faxNumber: faxNumber || "",
      links:
        links?.map((l: any) => ({
          platform: l.platform,
          url: l.url,
        })) ?? [],
      emails,
      url,
      images,
      displayOnBookingPage,
      claimStatus,
    };

    return {
      businessBasic,
      careMeta,
      careSettings,
      residents,
      businessStaffs: staffs ?? [],
      roles:
        roles?.map((r: any) => ({
          id: Number(r.id),
          name: r.name,
          description: r.description,
          permissions: JSON.stringify(r.permissions),
        })) ?? [],
      plans:
        plans?.map((p: any) => ({
          id: p.id,
          code: p.code,
          name: p.name,
          description: p.description,
          features: p.features,
          capabilities: p.capabilities,
          isActive: p.isActive,
        })) ?? [],
      businessAddress: businessAddress ?? null,
      workingHourGroups: workingHourGroups ?? [],
      businessBookingTypes: businessBookingTypes ?? [],
      ...rest,
    };
  });
};

const useLoadReduxStoreData = (
  queryName: BusinessQueryNames,
  variables: any = {}
) => {
  const dispatch = useDispatch();
  const queryDocument = queryDocuments[queryName];
  const { enabled = true, ...queryVariables } = variables || {};
  const sanitizedQueryVariables = Object.fromEntries(
    Object.entries(queryVariables).filter(([, value]) => value !== undefined)
  );
  const { isLoggedIn } = useAuthStatus();
  const userContext = useContext(UserContext);
  const defaultBusinessId = userContext.providerUser?.defaultBusinessId;

  const skipQuery =
    !enabled ||
    (queryName === BusinessQueryNames.GET_MY_BUSINESSES && !isLoggedIn);

  const { loading, error, data, refetch } = useQuery(queryDocument, {
    variables: sanitizedQueryVariables,
    skip: skipQuery,
    fetchPolicy:
      queryName === BusinessQueryNames.GET_MY_BUSINESSES
        ? "network-only"
        : undefined,
    nextFetchPolicy:
      queryName === BusinessQueryNames.GET_MY_BUSINESSES
        ? "cache-first"
        : undefined,
  });

  useEffect(() => {
    if (!loading && !error && data) {
      let businessResult;

      switch (queryName) {
        case BusinessQueryNames.GET_BUSINESSES:
          businessResult = data.businesses;
          break;
        case BusinessQueryNames.GET_MY_BUSINESSES:
          businessResult = data.myBusinesses;
          break;
        default:
          businessResult = null;
      }

      if (businessResult) {
        const businesses = normalizeBusinessData(businessResult);

        if (queryName === BusinessQueryNames.GET_MY_BUSINESSES) {
          dispatch(addBusinesses(businesses, "MY_BUSINESSES"));
          let preferredBusinessId = Number(defaultBusinessId || 0);
          try {
            const persisted = Number(localStorage.getItem("selectedBusinessId") || 0);
            const persistedExists = persisted
              ? businesses.some(
                  (business) => Number(business?.businessBasic?.id) === Number(persisted)
                )
              : false;
            if (persistedExists) {
              preferredBusinessId = persisted;
            }
          } catch (error) {
            // ignore storage failures
          }

          preferredBusinessId && dispatch(setSelectedBusiness(preferredBusinessId));
        } else if (queryName === BusinessQueryNames.GET_BUSINESSES) {
          dispatch(addBusinesses(businesses, "ALL_BUSINESSES"));
        }
      }
    }

    // ✅ Only log error once loading is complete
    if (!loading && error) {
      console.error("Error loading data:", error.message);
    }
  }, [loading, error, data, dispatch, queryName, defaultBusinessId]);

  return { loading, error, refetch };
};

export default useLoadReduxStoreData;
